/*
 * Product Row interactions — scoped per [data-slider][data-slider-type="product-row"] root.
 * Hooks: [data-slider], [data-slider-track], [data-slider-prev/next], [data-media-card].
 */

const style = getComputedStyle(document.documentElement);
const DESKTOP_BREAKPOINT = parseInt(style.getPropertyValue('--breakpoint-lg'), 10) || 1024;

class ProductRow {
  constructor(root) {
    this.root = root;
    this.track = root.querySelector('[data-slider-track]');
    this.controls = root.querySelector('[data-slider-controls]');
    this.prevBtn = root.querySelector('[data-slider-prev]');
    this.nextBtn = root.querySelector('[data-slider-next]');
    this.slideCount = parseInt(root.dataset.slideCount, 10) || 0;
    this.desktopVisible = parseInt(root.dataset.sliderDesktopVisible, 10) || 2;

    this.currentIndex = 0;
    this.itemsVisible = 1;
    this.initialOffset = 0;

    if (!this.track || this.slideCount === 0) return;

    this.init();
  }

  init() {
    this.track.setAttribute('data-slider-track-init', 'true');

    this.updateLayout();
    this.calculateInitialOffset();
    this.bindEvents();
    this.updateState();
  }

  calculateInitialOffset() {
    const firstChild = this.track.querySelector('[data-media-card]');
    if (firstChild) {
      this.initialOffset = firstChild.offsetLeft;
    }
  }

  bindEvents() {
    this.prevBtn?.addEventListener('click', () => this.goTo(this.currentIndex - 1));
    this.nextBtn?.addEventListener('click', () => this.goTo(this.currentIndex + 1));

    let scrollTimer;
    this.track.addEventListener(
      'scroll',
      () => {
        if (window.innerWidth >= DESKTOP_BREAKPOINT) return;

        if (scrollTimer) cancelAnimationFrame(scrollTimer);
        scrollTimer = requestAnimationFrame(() => {
          const cardWidth =
            this.track.querySelector('[data-media-card]')?.offsetWidth || this.track.offsetWidth;
          const snappedIndex = Math.round(this.track.scrollLeft / cardWidth);

          if (snappedIndex !== this.currentIndex) {
            this.currentIndex = snappedIndex;
            this.updateState();
          }
        });
      },
      { passive: true },
    );

    this._resizeTimer = null;
    this._windowWidth = window.innerWidth;
    this._onResize = () => {
      // Prevent iOS vertical scroll from triggering resize logic (address bar hide/show)
      if (window.innerWidth === this._windowWidth) return;
      this._windowWidth = window.innerWidth;

      if (this._resizeTimer) cancelAnimationFrame(this._resizeTimer);
      this._resizeTimer = requestAnimationFrame(() => {
        this.updateLayout();
        this.calculateInitialOffset();
        this.goTo(this.currentIndex);
        this.updateState();
      });
    };
    window.addEventListener('resize', this._onResize, { passive: true });
  }

  destroy() {
    if (this._onResize) window.removeEventListener('resize', this._onResize);
    if (this._resizeTimer) cancelAnimationFrame(this._resizeTimer);
  }

  updateLayout() {
    const isDesktop = window.innerWidth >= DESKTOP_BREAKPOINT;
    this.itemsVisible = isDesktop ? this.desktopVisible : 1;
    if (!this.controls) return;
    // Hide controls on mobile/tablet (swipe instead) and on desktop when all
    // cards already fit — no swiping is needed.
    const hideControls = !isDesktop || this.slideCount <= this.itemsVisible;
    this.controls.classList.toggle('hidden', hideControls);
    this.controls.classList.toggle('flex', !hideControls);
    // FAILS §2 / shared-invariant 6 — never aria-hidden="false"; pair aria-hidden with inert.
    if (hideControls) { this.controls.setAttribute('aria-hidden', 'true'); }
    else { this.controls.removeAttribute('aria-hidden'); }
    this.controls.toggleAttribute('inert', hideControls);
  }

  goTo(index) {
    const children = this.track.querySelectorAll('[data-media-card]');
    if (!children.length) return;

    this.currentIndex = Math.max(0, Math.min(index, this.slideCount - this.itemsVisible));
    const targetChild = children[this.currentIndex];

    if (targetChild) {
      const offset = targetChild.offsetLeft;

      const isDesktop = window.innerWidth >= DESKTOP_BREAKPOINT;
      this.track.style.transform = isDesktop ? `translateX(-${offset - this.initialOffset}px)` : '';
      if (!isDesktop) this.track.scrollLeft = offset;
    }

    this.updateState();
  }

  updateState() {
    this.setButtonState(this.prevBtn, this.currentIndex === 0);
    this.setButtonState(this.nextBtn, this.currentIndex >= this.slideCount - this.itemsVisible);
  }

  setButtonState(btn, isDisabled) {
    if (!btn) return;
    btn.toggleAttribute('disabled', isDisabled);
    btn.setAttribute('aria-disabled', String(isDisabled));
  }
}

function initSections(container = document) {
  const selector = '[data-slider][data-slider-type="product-row"]:not([data-slider-init])';
  const roots = container.matches?.(selector) ? [container] : container.querySelectorAll(selector);

  roots.forEach((root) => {
    root.dataset.sliderInit = 'true';
    root._instance = new ProductRow(root);
  });
}

initSections();

document.addEventListener('shopify:section:load', (e) => initSections(e.target));
document.addEventListener('shopify:section:unload', (e) => {
  const root = e.target.querySelector?.('[data-slider][data-slider-type="product-row"]') || e.target;
  if (root?._instance) {
    root._instance.destroy();
    delete root._instance;
  }
});
